/* Session expiry, handled once for the whole portal.
 *
 * The portal session is 2 hours of IDLE time (server: PORTAL_SESSION_TTL). ProtectedRoute checks
 * auth when a page MOUNTS and never again, so a tab left open all morning still looks signed in:
 * the tables are on screen, the buttons are live. The first action after the cookie expires gets a
 * 401 from the API, and every screen showed that raw — Grace hit "Unauthorized, token missing" when
 * she deleted a ticket she was perfectly entitled to delete (Max, 2026-08-25).
 *
 * So a 401 is intercepted in ONE place and turned into what it actually means: sign in again, and
 * come back to the page you were on. It is done for axios and for fetch because the portal uses both
 * — wrapping the two transports is a handful of lines, while adding a check to every one of the
 * hundred-odd call sites would leave the next one to forget it again.
 *
 * Deliberately NOT redirected:
 *   - the public ticket link (/api/development/public/…), which has no session and answers 404
 *   - anything while already on /login, which would loop
 *   - 403: that is "you may not do this", a real answer the screen should show as-is
 */
import axios from 'axios';
import { reportUnreachable } from './loopCapture';

const LOGIN_PATH = '/login';

const onLoginPage = () => window.location.pathname.startsWith(LOGIN_PATH);

// One redirect only: several calls on a page fail together when a session ends.
let redirecting = false;

const isPublicApi = (url) => String(url || '').includes('/api/development/public/');

const goToLogin = () => {
    if (redirecting || onLoginPage()) return;
    redirecting = true;
    const returnTo = `${window.location.pathname}${window.location.search}${window.location.hash}`;
    // No error code: the login page used to print "your session ended…" for it, which Jay asked to go
    // (2026-10-19). The page still sends the person back to returnTo once they are signed in.
    window.location.replace(`${LOGIN_PATH}?returnTo=${encodeURIComponent(returnTo)}`);
};

/* OUR OWN API, on this site: only those calls carry the screen header (a header on a call to another site would make the
   browser ask that site's permission first) and only their failures are the improvement loop's */
const ownApi = (url) => {
    const u = String(url || '');
    return u.includes('/api/') && (u.startsWith('/') || u.startsWith(window.location.origin));
};
const GATEWAY = new Set([502, 503, 504]);

export const installSessionGuard = () => {
    /* WHICH SCREEN ASKED (Jay #1137): a server error is counted with the screen whose call raised it — X-Portal-Screen */
    axios.interceptors.request.use((cfg) => {
        try {
            if (ownApi(cfg.url)) {
                const v = window.location.pathname.slice(0, 200);
                if (cfg.headers && typeof cfg.headers.set === 'function') cfg.headers.set('X-Portal-Screen', v);
                else cfg.headers = { ...(cfg.headers || {}), 'X-Portal-Screen': v };
            }
        } catch (e) { /* as it was */ }
        return cfg;
    });
    axios.interceptors.response.use(
        (res) => res,
        (err) => {
            const status = err?.response?.status;
            const url = err?.config?.url;
            if (status === 401 && !isPublicApi(url)) goToLogin();
            // the request never reached the backend: nobody else can count it
            if (ownApi(url) && (!err?.response || GATEWAY.has(status)) && err?.code !== 'ERR_CANCELED') reportUnreachable(url, status);
            return Promise.reject(err);
        },
    );

    const nativeFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
        const url = typeof input === 'string' ? input : (input && input.url) || '';
        let opts = init;
        if (typeof input === 'string' && ownApi(url)) {
            try {
                const headers = new Headers((init && init.headers) || {});
                if (!headers.has('X-Portal-Screen')) headers.set('X-Portal-Screen', window.location.pathname.slice(0, 200));
                opts = { ...(init || {}), headers };
            } catch (e) { opts = init; }
        }
        let res;
        try { res = await nativeFetch(input, opts); } catch (e) {
            if (ownApi(url) && e?.name !== 'AbortError') reportUnreachable(url, 0);
            throw e;
        }
        // Only our own API: a 401 from anywhere else is not this portal's session.
        if (res.status === 401 && url.includes('/api/') && !isPublicApi(url)) goToLogin();
        if (ownApi(url) && GATEWAY.has(res.status)) reportUnreachable(url, res.status);
        return res;
    };
};

export default installSessionGuard;
